import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { currencySymbol, formatCurrency } from '@/helpers/Currency';
import { getTotalFees } from '@/helpers/Fees';
import { useFlexiblePrice } from '@/helpers/TicketType';
import Price from '@/components/elements/Price.vue';
import FormInput2 from '@/components/forms/FormInput2.vue';
import TixStepper from '@/components/forms/TixStepper.vue';
import { languageItem } from '@/language/helpers';
import { openDescriptionModal } from '@/modals/descriptionModal';
@Component({
    name: 'TicketTypeSteppers',
    components: { Price, FormInput2, TixStepper },
})
export default class default_1 extends Vue {
    @Prop({ required: true })
    ticketType;
    @Prop({ required: true })
    value;
    @Prop({ required: true })
    remainingCapacity;
    /**
     * Must be at least one.
     */
    @Prop({ required: true })
    minQuantity;
    @Prop({ default: false })
    exactlyOneTicket;
    @Prop()
    soldOut;
    @Prop({ default: false })
    giftOfMembership;
    @Prop({ default: false })
    showAdvertisedFees;
    selectedPrice = null;
    priceValidationError = null;
    l = languageItem('cartWidget');
    // Used for variable pricing tickets.
    get price() {
        return parseFloat(this.ticketType.currency_amount);
    }
    // Used for regular priced tickets.
    get priceWithFees() {
        return parseFloat(this.ticketType.currency_amount) + this.totalFees;
    }
    get discountedPrice() {
        const discount = this.ticketType.discounted_amount;
        if (discount != null) {
            return parseFloat(discount) + this.totalFees;
        }
        else {
            return null;
        }
    }
    get fees() {
        const fees = this.ticketType.outside_fees;
        if (this.showAdvertisedFees && fees?.length) {
            return fees;
        }
        return [];
    }
    get totalFees() {
        return getTotalFees(this.fees);
    }
    get formattedFees() {
        if (this.totalFees === 0) {
            return null;
        }
        const fees = {
            fee_fixed_outside: 0,
            fee_percent_outside: 0,
        };
        for (const fee of this.fees) {
            fees[fee.name] += parseFloat(fee.fee);
        }
        return {
            fee_fixed_outside: this.formatFee(fees.fee_fixed_outside, this.l.fixedFees),
            fee_percent_outside: this.formatFee(fees.fee_percent_outside, this.l.percentFees),
        };
    }
    formatFee(total, name) {
        if (!total)
            return null;
        return `${formatCurrency(total)} ${name}`;
    }
    get fixedFees() {
        return this.formattedFees?.fee_fixed_outside ?? null;
    }
    get percentFees() {
        return this.formattedFees?.fee_percent_outside ?? null;
    }
    get hasDiscount() {
        return this.discountedPrice !== null;
    }
    get useFlexiblePrice() {
        return useFlexiblePrice(this.ticketType);
    }
    onPriceValidation(error) {
        this.priceValidationError = error ? 'Value entered is outside min/max' : null;
    }
    @Watch('value', { deep: true, immediate: true })
    onValueChange(value) {
        this.selectedPrice = value.price != null ? value.price : String(this.price);
    }
    @Watch('selectedPrice')
    changePrice(inputAmount) {
        const value = {
            ticketTypeId: this.ticketType.id,
            price: inputAmount,
        };
        this.$emit('priceInput', value);
    }
    get nextIncrement() {
        if (this.remainingCapacity <= 0) {
            return 0;
        }
        else if (this.value.quantity === 0) {
            return this.minQuantity;
        }
        else {
            return 1;
        }
    }
    get nextDecrement() {
        if (this.value.quantity === 0 || this.exactlyOneTicket) {
            return 0;
        }
        else if (this.value.quantity === this.minQuantity) {
            return this.minQuantity;
        }
        else {
            return 1;
        }
    }
    get summary() {
        const { summary, description } = this.ticketType;
        if (summary) {
            return summary;
        }
        else if (description) {
            return description;
        }
        return null;
    }
    get description() {
        const { summary, description } = this.ticketType;
        if (summary && description) {
            return description;
        }
        return null;
    }
    openDescriptionModal() {
        const isTicketTypeOfGom = this.giftOfMembership && this.ticketType.group?.handler === 'codes';
        const title = isTicketTypeOfGom ? `${this.ticketType.name} benefits` : '';
        openDescriptionModal(this.description, { title });
    }
    get buttonLabel() {
        return this.giftOfMembership ? this.$t('membershipLevel.showMoreLabel') : 'More';
    }
    get countLabel() {
        return this.$tc('ticketTypeSteppers.countLabel', this.value.quantity, { name: this.ticketType.name });
    }
    get currencySymbol() {
        return currencySymbol();
    }
}
